import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { SkillChange } from './registryApi';

/** A per-practice skill override (agent-suite row; clientId = locationId), with its status. */
export type SkillOverrideStatus = 'active' | 'expired' | 'retired';

export interface SkillOverride {
  id: string;
  clientId: string;
  skillKey: string;
  method?: string;
  references?: Record<string, string>;
  objective?: string;
  reason: string;
  expiresAt?: number;
  createdBy?: string;
  createdAt: number;
  updatedAt: number;
  retiredAt?: number;
  retiredReason?: string;
  status: SkillOverrideStatus;
}

export interface SkillOverrideRow extends SkillOverride {
  location: { id: string; name: string };
  practice: { id: string; name: string };
}

export interface SkillOverrideFiles {
  override: SkillOverride;
  global: Record<string, string>;
  effective: Record<string, string>;
}

export interface CreateSkillOverrideInput {
  skillKey: string;
  method?: string;
  references?: Record<string, string>;
  objective?: string;
  reason: string;
  expiresAt?: number;
}

const base = (practiceId: string, locationId: string) =>
  `/agency/practices/${practiceId}/locations/${locationId}/skill-overrides`;

export const overridesApi = {
  listAll: async (): Promise<SkillOverrideRow[]> => {
    const { data } = await apiClient.get<ApiResponse<SkillOverrideRow[]>>('/agency/skill-overrides');
    return data.data;
  },

  listForLocation: async (practiceId: string, locationId: string, includeInactive = false): Promise<SkillOverride[]> => {
    const { data } = await apiClient.get<ApiResponse<SkillOverride[]>>(base(practiceId, locationId), {
      params: includeInactive ? { includeInactive: 'true' } : {},
    });
    return data.data;
  },

  create: async (practiceId: string, locationId: string, input: CreateSkillOverrideInput): Promise<SkillOverride> => {
    const { data } = await apiClient.post<ApiResponse<SkillOverride>>(base(practiceId, locationId), input);
    return data.data;
  },

  retire: async (practiceId: string, locationId: string, id: string, reason?: string): Promise<void> => {
    await apiClient.post(`${base(practiceId, locationId)}/${encodeURIComponent(id)}/retire`, { reason });
  },

  files: async (practiceId: string, locationId: string, id: string): Promise<SkillOverrideFiles> => {
    const { data } = await apiClient.get<ApiResponse<SkillOverrideFiles>>(`${base(practiceId, locationId)}/${encodeURIComponent(id)}/files`);
    return data.data;
  },

  promote: async (practiceId: string, locationId: string, id: string, input: { title: string; summary?: string }): Promise<SkillChange> => {
    const { data } = await apiClient.post<ApiResponse<SkillChange>>(`${base(practiceId, locationId)}/${encodeURIComponent(id)}/promote`, input);
    return data.data;
  },
};
